import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Divider,
  Paper,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { UnitTypeCard } from "./UnitTypeCard";

export function WingAccordion({
  wing,
  parkingTypes,
  loadingParkingTypes,
  unitConfigs,
  savedConfigs,
  onUnitConfigChange,
  onExpand,
  selectedWingId,
  hasUnsavedConfigs,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // ✅ NEW: Auto-close when NOT selected wing
  useEffect(() => {
    if (selectedWingId && selectedWingId !== wing.id) {
      setIsExpanded(false);
    }
  }, [selectedWingId, wing.id]);

  // ✅ NEW: Filter saved configs for THIS WING ONLY (internal filtering)
  const wingSavedConfigs = savedConfigs.filter(config => config.wingId === wing.id);

  const totalConfigs = wing.unitTypes.reduce(
    (sum, ut) => sum + (unitConfigs[ut.id]?.length || 0),
    0
  );

  // ✅ NEW: Count saved configs for this wing
  const savedConfigCount = wingSavedConfigs.length;

  const isActiveWing = selectedWingId === wing.id;
  const isSelectable = !hasUnsavedConfigs || isActiveWing;
  const hasActiveConfigs = wing.unitTypes.some(
    ut => unitConfigs[ut.id]?.length > 0
  );

  const handleToggle = () => {
    // ✅ Block if not selectable
    if (!isSelectable) return;

    setIsExpanded(prev => !prev);

    if (!isExpanded && onExpand) {
      onExpand(wing.id);
    }
  };

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        borderRadius: 2, 
        mb: 2,
        opacity: isSelectable ? 1 : 0.6,
        borderColor: isActiveWing ? "primary.main" : "divider",
        borderWidth: isActiveWing ? 2 : 1,
        bgcolor: !isSelectable ? "grey.50" : "transparent"
      }}
    >
      <Box 
        px={3} 
        py={2.5} 
        onClick={handleToggle} 
        sx={{ 
          cursor: isSelectable ? "pointer" : "not-allowed",
          bgcolor: isActiveWing ? "primary.50" : "transparent"
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={2}>
            <Box
              width={44}
              height={44}
              borderRadius={2}
              bgcolor={isActiveWing || hasActiveConfigs ? "primary.main" : "#fafbff"}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ApartmentIcon sx={{ 
                color: isActiveWing || hasActiveConfigs ? "white" : "#3B5BFF" 
              }} />
            </Box>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: 17, fontWeight: 800 }}>
                  {wing.name}
                </Typography>
                
                {isActiveWing && (
                  <Chip 
                    label="ACTIVE" 
                    size="small" 
                    color="primary" 
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                
                {totalConfigs > 0 && (
                  <Chip 
                    label={`${totalConfigs} unsaved`} 
                    size="small" 
                    variant="outlined" 
                    color="info"
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}

                {savedConfigCount > 0 && (
                  <Chip 
                    label={`${savedConfigCount} saved`} 
                    size="small" 
                    color="success"
                    variant="outlined"
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
              </Stack>
              
              {!isSelectable && hasUnsavedConfigs && (
                <Typography sx={{ fontSize: 12, mt: 0.5 }} color="error.main">
                  <strong>⚠️ Save/clear other wing first</strong>
                </Typography>
              )}
            </Box>
          </Stack>

          <IconButton disabled={!isSelectable}>
            <ExpandMoreIcon
              sx={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0)",
                transition: "0.2s",
                color: isSelectable ? "inherit" : "grey.400"
              }}
            />
          </IconButton>
        </Stack>
      </Box>

      <Collapse in={isExpanded}>
        <Divider />
        <Box px={3} py={3} >
          {wing.unitTypes.length === 0 ? (
            <Typography sx={{ fontSize: 14 }} color="text.secondary" mb={2}>
              No unit types loaded yet...
            </Typography>
          ) : (
            <Stack spacing={2} mb={3}>
              {wing.unitTypes.map((ut) => {
                // ✅ NEW: Get saved configs for THIS unit type in THIS wing
                const unitSavedConfigs = wingSavedConfigs.filter(
                  config => config.unitTypeId === ut.id
                );
                
                return (
                  <UnitTypeCard
                    key={ut.id}
                    unitType={ut}
                    parkingTypes={parkingTypes} 
                    configs={unitConfigs[ut.id] || []}
                    savedConfigs={unitSavedConfigs}  // ✅ Pass wing-specific saved configs
                    disabled={!isSelectable || loadingParkingTypes}
                    onConfigChange={(configs) => {
                      if (!isSelectable) return;
                      onUnitConfigChange(ut.id, configs);
                    }}
                  />
                );
              })}
            </Stack>
          )}

          {loadingParkingTypes ? (
            <Typography sx={{ fontSize: 14 }} color="text.secondary">
              Loading parking types for {wing.name}...
            </Typography>
          ) : parkingTypes.length === 0 ? (
            <Box p={2} bgcolor="#fafbff" borderRadius={1} border="1px solid" borderColor="warning.light">
              <Typography sx={{ fontSize: 13 }} color="warning.main">
                No parking types available for Wing {wing.name}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Collapse>
    </Paper>
  );
}
